import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Auth({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const searchParams = new URLSearchParams(location.search);
  const defaultRole = searchParams.get('role') || 'client';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    identification: '',
    role: defaultRole
  });

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    // Persist chosen role so backend can assign it if the user is new
    try { sessionStorage.setItem('pending_google_role', formData.role || 'client'); } catch (_) {}
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLogin && (!acceptedPolicy || !acceptedTerms)) {
      setShowPrivacyPolicy(true);
      return;
    }

    setLoading(true);

    try {
      const endpoint = isLogin ? `${API}/auth/login` : `${API}/auth/register`;
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(endpoint, payload);
      const { access_token, user } = response.data;

      toast.success(isLogin ? '¡Bienvenido!' : '¡Cuenta creada exitosamente!');
      onLogin(access_token, user);
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al procesar la solicitud';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#3A4F6C] via-slate-800 to-[#2A3B4F] flex items-center justify-center p-4">
      {/* Legal Documents Modal */}
      <Dialog open={showPrivacyPolicy} onOpenChange={setShowPrivacyPolicy}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="legal-documents-modal">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-2xl font-bold text-[#3A4F6C]">
              TÉRMINOS LEGALES - CONSTRU/CLIC
            </DialogTitle>
            <DialogDescription>
              Por favor lee y acepta los siguientes documentos para continuar
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="privacy" className="w-full flex-1 flex flex-col min-h-0 overflow-hidden">
            <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
              <TabsTrigger value="privacy">Política de Privacidad</TabsTrigger>
              <TabsTrigger value="terms">Términos y Condiciones</TabsTrigger>
            </TabsList>

            {/* Privacy Policy Tab */}
            <TabsContent value="privacy" className="flex-1 min-h-0 overflow-hidden">
              <ScrollArea className="h-[200px] sm:h-[250px] pr-4 border rounded-md p-2">
                <div className="space-y-4 text-sm text-slate-700">
                  <div>
                    <h3 className="font-bold text-[#3A4F6C] text-lg mb-2">
                      POLÍTICA DE TRATAMIENTO DE DATOS PRIVACIDAD Y HABEAS DATA
                    </h3>
                    <p className="font-semibold text-slate-500 mb-2">
                      FECHA DE ÚLTIMA ACTUALIZACIÓN: Febrero 2026
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#3A4F6C] mb-2">1. OBJETIVO Y MARCO LEGAL</h3>
                    <p>
                      En cumplimiento de la Ley 1581 de 2012 y el Decreto 1377 de 2013 de la República de Colombia, 
                      la plataforma tecnológica Constru/clic (en adelante, la "PLATAFORMA") establece la presente 
                      política para garantizar el derecho constitucional al Habeas Data de los usuarios que solicitan 
                      servicios (en adelante, el "CLIENTE").
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#3A4F6C] mb-2">2. DATOS QUE RECOPILAMOS</h3>
                    <p className="mb-2">
                      Para el correcto funcionamiento de la aplicación tipo "On-Demand", Constru/clic recopilará, 
                      almacenará y procesará los siguientes datos personales del CLIENTE:
                    </p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Nombre completo y número de documento de identidad.</li>
                      <li>Número de teléfono celular y correo electrónico.</li>
                      <li>Dirección de residencia o del lugar donde se ejecutará la obra.</li>
                      <li>Datos de geolocalización en tiempo real (GPS) al momento de solicitar el servicio.</li>
                      <li>Historial de servicios solicitados y calificaciones otorgadas.</li>
                    </ul>
                    <p className="mt-2 text-xs italic">
                      *Nota: Los datos de tarjetas de crédito o débito son procesados directamente por la pasarela 
                      de pagos aliada y Constru/clic no los almacena en sus servidores.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#3A4F6C] mb-2">3. FINALIDAD DEL TRATAMIENTO DE DATOS</h3>
                    <p className="mb-2">
                      Los datos personales del CLIENTE serán utilizados exclusivamente para los siguientes fines:
                    </p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Conectar al CLIENTE con el trabajador (Socio Experto) más cercano y adecuado para su requerimiento.</li>
                      <li>Compartir la dirección y número de contacto con el Socio Experto asignado para que pueda desplazarse y ejecutar la obra o reparación.</li>
                      <li>Procesar los pagos y generar los recibos correspondientes.</li>
                      <li>Atender solicitudes de soporte, quejas, reclamos o garantías.</li>
                      <li>Enviar notificaciones sobre el estado del servicio en tiempo real.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#3A4F6C] mb-2">4. DERECHOS DEL TITULAR (EL CLIENTE)</h3>
                    <p className="mb-2">
                      Como titular de los datos, el CLIENTE tiene derecho en todo momento a:
                    </p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Conocer, actualizar y rectificar sus datos personales frente a la PLATAFORMA.</li>
                      <li>Solicitar prueba de la autorización otorgada para el tratamiento de sus datos.</li>
                      <li>Revocar la autorización y/o solicitar la supresión de sus datos cuando no exista un deber legal o contractual de permanecer en la base de datos de Constru/clic.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#3A4F6C] mb-2">5. AUTORIZACIÓN Y COMPARTICIÓN DE DATOS</h3>
                    <p>
                      Al registrarse en la aplicación, el CLIENTE autoriza de manera expresa e inequívoca a 
                      Constru/clic para compartir estrictamente los datos necesarios (nombre, teléfono y dirección) 
                      con el contratista independiente (Socio Experto) que haya aceptado el servicio, con el único 
                      fin de permitir la ejecución del trabajo solicitado.
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Terms and Conditions Tab */}
            <TabsContent value="terms" className="flex-1 min-h-0 overflow-hidden">
              <ScrollArea className="h-[200px] sm:h-[250px] pr-4 border rounded-md p-2">
                <div className="space-y-4 text-sm text-slate-700">
                  <div>
                    <h3 className="font-bold text-[#3A4F6C] text-lg mb-2">
                      TÉRMINOS Y CONDICIONES DE USO - USUARIO CLIENTE
                    </h3>
                    <p className="font-semibold text-slate-500 mb-2">
                      FECHA DE ÚLTIMA ACTUALIZACIÓN: Febrero 2026
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#3A4F6C] mb-2">1. DESCRIPCIÓN DEL SERVICIO Y ROL DE LA PLATAFORMA</h3>
                    <p className="mb-2">
                      Constru/clic es una plataforma tecnológica (Marketplace) que facilita el contacto entre usuarios 
                      que requieren servicios de construcción, mantenimiento o reparación locativa (en adelante, el "CLIENTE") 
                      y profesionales o ayudantes independientes capacitados para prestarlos (en adelante, el "SOCIO EXPERTO").
                    </p>
                    <p>
                      El CLIENTE reconoce y acepta que Constru/clic NO es una empresa de construcción, contratista, ni empleador 
                      de los SOCIOS EXPERTOS. La plataforma actúa exclusivamente como un intermediario tecnológico para facilitar 
                      la conexión, geolocalización y el recaudo de los pagos.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#3A4F6C] mb-2">2. INDEPENDENCIA Y RESPONSABILIDAD DEL SERVICIO</h3>
                    <p className="mb-2">
                      Los SOCIOS EXPERTOS son contratistas independientes. Por lo tanto, el CLIENTE acepta que la responsabilidad 
                      total por la ejecución, calidad, idoneidad, seguridad y garantía de la obra o reparación recae exclusivamente 
                      sobre el SOCIO EXPERTO que ejecutó el servicio.
                    </p>
                    <p>
                      Constru/clic no asume responsabilidad civil, penal ni administrativa por daños a la propiedad, retrasos, 
                      perjuicios o mala praxis derivados de la ejecución del trabajo. Cualquier reclamo por garantía deberá ser 
                      gestionado directamente entre el CLIENTE y el SOCIO EXPERTO.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#3A4F6C] mb-2">3. TARIFAS Y MÉTODO DE PAGO</h3>
                    <p className="mb-2">
                      Al solicitar un servicio a través de Constru/clic, el CLIENTE visualizará una tarifa base o un precio estimado, 
                      el cual puede variar dependiendo de la complejidad real del trabajo (ej. cambio de repuestos, horas adicionales).
                    </p>
                    <p>
                      El CLIENTE se compromete a realizar el pago total del servicio única y exclusivamente a través de los canales 
                      de pago autorizados dentro de la aplicación (tarjeta de crédito, débito, PSE o billeteras digitales). Evadir 
                      el pago a través de la plataforma causará el bloqueo inmediato de la cuenta del CLIENTE.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#3A4F6C] mb-2">4. VERIFICACIÓN DE PERFILES</h3>
                    <p>
                      Constru/clic realiza un esfuerzo razonable por verificar la identidad y las certificaciones de los SOCIOS EXPERTOS 
                      (Tarjetas profesionales, cursos de alturas, etc.) antes de activar sus perfiles. Sin embargo, el CLIENTE es 
                      responsable de tomar medidas de precaución básicas al permitir el ingreso de terceros a su domicilio o lugar de obra.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#3A4F6C] mb-2">5. POLÍTICA DE CANCELACIÓN Y EMERGENCIAS</h3>
                    <p className="mb-2">
                      Si el CLIENTE cancela un servicio cuando el SOCIO EXPERTO ya se encuentra en camino o en el lugar de la obra, 
                      Constru/clic podrá cobrar una tarifa de cancelación para compensar el tiempo y desplazamiento del trabajador.
                    </p>
                    <p>
                      En caso de emergencias (ej. fugas graves de agua o gas), el CLIENTE entiende que los tiempos de llegada son 
                      estimados y dependen del tráfico y la disponibilidad en Bogotá, por lo que Constru/clic no garantiza tiempos 
                      de respuesta exactos.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#3A4F6C] mb-2">6. ACEPTACIÓN DE LOS TÉRMINOS</h3>
                    <p>
                      Al registrarse, solicitar un servicio o navegar por la aplicación Constru/clic, el CLIENTE declara haber leído, 
                      entendido y aceptado incondicionalmente los presentes Términos y Condiciones, así como la Política de Tratamiento 
                      de Datos Personales.
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>

          {/* Fixed footer with checkboxes and buttons */}
          <div className="flex-shrink-0 border-t-2 border-slate-200 pt-4 mt-4 space-y-3 bg-white relative z-10">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <Checkbox
                id="accept-policy"
                checked={acceptedPolicy}
                onCheckedChange={setAcceptedPolicy}
                data-testid="accept-policy-checkbox"
                className="h-5 w-5 flex-shrink-0"
              />
              <label
                htmlFor="accept-policy"
                className="text-sm leading-tight cursor-pointer select-none"
              >
                He leído y acepto la <span className="font-bold text-[#FF8935]">Política de Tratamiento de Datos</span>
              </label>
            </div>

            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <Checkbox
                id="accept-terms"
                checked={acceptedTerms}
                onCheckedChange={setAcceptedTerms}
                data-testid="accept-terms-checkbox"
                className="h-5 w-5 flex-shrink-0"
              />
              <label
                htmlFor="accept-terms"
                className="text-sm leading-tight cursor-pointer select-none"
              >
                He leído y acepto los <span className="font-bold text-[#FF8935]">Términos y Condiciones de Uso</span>
              </label>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPrivacyPolicy(false);
                  setAcceptedPolicy(false);
                  setAcceptedTerms(false);
                }}
                className="flex-1"
                data-testid="cancel-policy-button"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (acceptedPolicy && acceptedTerms) {
                    setShowPrivacyPolicy(false);
                    handleSubmit(new Event('submit'));
                  } else {
                    toast.error('Debes aceptar ambos documentos para continuar');
                  }
                }}
                disabled={!acceptedPolicy || !acceptedTerms}
                className="flex-1 bg-[#FF8935] hover:bg-[#E67A2E] text-white font-bold"
                data-testid="accept-and-continue-button"
              >
                ✓ Aceptar y Continuar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="w-full max-w-md shadow-2xl" data-testid="auth-card">
        <CardHeader className="space-y-1">
          <CardTitle className="text-3xl font-bold text-center">
            {isLogin ? 'INICIAR SESIÓN' : 'CREAR CUENTA'}
          </CardTitle>
          <CardDescription className="text-center">
            {isLogin ? 'Ingresa tus credenciales' : 'Completa el formulario para registrarte'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="full_name">Nombre Completo</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    data-testid="input-full-name"
                    placeholder="Juan Pérez"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="identification">Cédula/Identificación</Label>
                  <Input
                    id="identification"
                    name="identification"
                    data-testid="input-identification"
                    placeholder="1234567890"
                    value={formData.identification}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    name="phone"
                    data-testid="input-phone"
                    placeholder="+57 300 123 4567"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Tipo de Cuenta</Label>
                  <select
                    id="role"
                    name="role"
                    data-testid="select-role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                    required
                  >
                    <option value="client">Cliente (Busco servicios)</option>
                    <option value="worker">Trabajador (Ofrezco servicios)</option>
                    <option value="both">Ambos</option>
                    <option value="property_admin">Administrador de Conjunto/Hotel</option>
                    <option value="affiliate_admin">Administrador Afiliado (cashback 5%)</option>
                  </select>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Correo Electrónico</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="input-email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="input-password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="submit-button"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-6 rounded-full"
              disabled={loading}
            >
              {loading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta')}
            </Button>
          </form>

          {/* Divider */}
          <div className="my-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">o</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Google Login */}
          <Button
            type="button"
            data-testid="google-login-button"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full py-6 rounded-full border-2 border-slate-200 hover:bg-slate-50 flex items-center justify-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.28 1.48-1.12 2.73-2.39 3.58v2.98h3.85c2.26-2.09 3.59-5.17 3.59-8.8z"/>
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.93l-3.85-2.98c-1.07.72-2.44 1.14-4.09 1.14-3.15 0-5.82-2.13-6.77-4.99H1.24v3.13C3.22 21.3 7.31 24 12 24z"/>
              <path fill="#FBBC05" d="M5.23 14.24c-.24-.72-.38-1.49-.38-2.24s.14-1.52.38-2.24V6.63H1.24C.45 8.24 0 10.06 0 12s.45 3.76 1.24 5.37l3.99-3.13z"/>
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.22 2.7 1.24 6.63l3.99 3.13C6.18 6.88 8.85 4.75 12 4.75z"/>
            </svg>
            <span className="font-medium text-slate-700">Continuar con Google</span>
          </Button>

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setIsLogin(!isLogin)}
              className="text-orange-600 hover:text-orange-700 font-medium"
            >
              {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión'}
            </button>
          </div>

          <div className="mt-4 text-center">
            <button
              data-testid="back-to-home"
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-700"
            >
              ← Volver al inicio
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}